import React, { useState, useEffect } from 'react';
import api from '../api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const MyHolidays = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('upcoming');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const typeLabels = { holiday: 'Holiday', sick: 'Sick Day', awol: 'Absent Without Leave' };
  const statusLabels = { pending: 'Pending', approved: 'Approved', declined: 'Declined', cancelled: 'Cancelled' };
  const todayStr = new Date().toISOString().slice(0, 10);
  const isUpcoming = (r) => r.start_date.substring(0, 10) > todayStr;

  const load = async () => {
    setLoading(true);
    try {
      // The balance card always shows the current holiday year (1 Apr to 31 Mar);
      // the list below can still be filtered by calendar year / upcoming / all.
      const [reqRes, balRes] = await Promise.all([
        api.get(`/api/holidays/my?year=${year}`),
        api.get(`/api/holidays/my/balance`)
      ]);
      setRequests(reqRes.data);
      setBalance(balRes.data);
    } catch (err) {
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await api.delete(`/api/holidays/${id}`);
      setSuccess('Request cancelled');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel request');
    }
  };

  const submitCancellation = async () => {
    try {
      await api.post(`/api/holidays/${cancelModal.id}/request-cancellation`, { reason: cancelReason });
      setSuccess('Cancellation requested — your manager has been notified.');
      setCancelModal(null);
      setCancelReason('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request cancellation');
    }
  };

  const years = [new Date().getFullYear() + 1, new Date().getFullYear(), new Date().getFullYear() - 1];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Holidays</h1>
          <p className="page-subtitle">View and manage your absence requests</p>
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={year} onChange={e => { const v = e.target.value; setYear(v === 'all' || v === 'upcoming' ? v : Number(v)); }}>
          <option value="upcoming">This year &amp; upcoming</option>
          <option value="all">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Balance summary */}
      {balance && user?.holiday_entitlement > 0 && (
        <div className="stat-grid" style={{ marginBottom: '28px' }}>
          <div className="stat-card red-accent">
            <div className="stat-label">Entitlement</div>
            <div className="stat-value">{balance.entitlement}</div>
            <div className="stat-sub">holiday year {balance.holiday_year}</div>
          </div>
          <div className="stat-card navy-accent">
            <div className="stat-label">Remaining</div>
            <div className="stat-value">{balance.remaining}</div>
            <div className="stat-sub">approved days left</div>
          </div>
          <div className="stat-card success-accent">
            <div className="stat-label">Taken</div>
            <div className="stat-value">{balance.used}</div>
            <div className="stat-sub">approved days</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{balance.pending}</div>
            <div className="stat-sub">awaiting approval</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Absence History</h3>
          <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{requests.length} record{requests.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <h3>No absences recorded</h3>
            <p>No absence requests found for {year}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td><span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span></td>
                    <td>{format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'EEE d MMM yyyy')}</td>
                    <td>{format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'EEE d MMM yyyy')}</td>
                    <td>{r.days_taken}{r.half_day ? ` (½ ${r.half_day_period === 'pm' ? 'PM' : 'AM'})` : ''}</td>
                    <td>
                      <span className={`badge badge-${r.status}`}>{statusLabels[r.status] || r.status}</span>
                      {r.status === 'declined' && r.decline_reason && (
                        <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '4px' }}>{r.decline_reason}</div>
                      )}
                      {r.status === 'approved' && r.cancellation_requested && (
                        <div style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Cancellation requested</div>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{r.notes || '-'}</td>
                    <td>
                      {r.status === 'pending' && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleCancel(r.id)}>Cancel</button>
                      )}
                      {r.status === 'approved' && r.absence_type === 'holiday' && isUpcoming(r) && !r.cancellation_requested && (
                        <button className="btn btn-outline btn-sm" onClick={() => { setCancelModal(r); setCancelReason(''); setError(''); }}>Request cancellation</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelModal && (
        <div className="modal-overlay" onClick={() => setCancelModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Request holiday cancellation</h3>
              <button className="modal-close" onClick={() => setCancelModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                You're asking to cancel your approved holiday from{' '}
                <strong style={{ color: 'var(--navy)' }}>{format(new Date(cancelModal.start_date.substring(0,10) + 'T00:00:00'), 'd MMM')}</strong> to{' '}
                <strong style={{ color: 'var(--navy)' }}>{format(new Date(cancelModal.end_date.substring(0,10) + 'T00:00:00'), 'd MMM yyyy')}</strong>{' '}
                ({cancelModal.days_taken} day{cancelModal.days_taken !== 1 ? 's' : ''}). Your manager will need to approve it, and the days will go back onto your allowance.
              </p>
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-control" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="e.g. plans changed, no longer need the time off" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCancelModal(null)}>Back</button>
              <button className="btn btn-primary" onClick={submitCancellation}>Request cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHolidays;
